import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CalendarCheck, LucideAngularModule } from 'lucide-angular';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { VenueService } from '@app/venues/shared/venue.service';
import { ShowRound } from './show-round.model';
import { showRoundReviewClasses } from './show-round-review.model';
import { ShowRoundService } from './show-round.service';

/**
 * TVG-337: every show round, one line each.
 *
 * Kate needs the viewings as a schedule rather than one contact at a time, so
 * this is the list form of what TVG-324 shows on the contact record — the same
 * bookings, read the other way round. Each line is one booking, so a contact
 * with a viewing at two venues appears twice, and each line opens the contact.
 *
 * The list opens on today and pages forward as it is scrolled, so what is
 * coming up is what you land on.
 */
@Component({
  selector: 'app-show-rounds-page',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule, RouterLink, LucideAngularModule, SelectComponent, TextInputComponent],
  templateUrl: './show-rounds-page.component.html',
})
export class ShowRoundsPageComponent extends Destroyer implements OnInit, AfterViewInit, OnDestroy {
  /** Sentinel below the last row; scrolling it into view fetches the next page. */
  @ViewChild('scrollSentinel') private scrollSentinel?: ElementRef<HTMLElement>;

  protected readonly CalendarCheckIcon = CalendarCheck;

  protected showRounds: ShowRound[] = [];
  protected venueChoices: { value: number | null; label: string }[] = [];
  protected loading = false;
  protected loadError: string | null = null;
  protected hasLoadedOnce = false;
  protected total = 0;

  protected searchControl = new FormControl('');
  protected venueControl = new FormControl<number | null>(null);

  private currentPage = 1;
  private lastPage = 1;
  private observer?: IntersectionObserver;

  private readonly pageSize = 50;

  constructor(
    private showRoundService: ShowRoundService,
    private venueService: VenueService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadVenues();
    this.load();

    // Debounced so a search is one request per pause in typing, not per key.
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.reload());

    this.venueControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.reload());
  }

  ngAfterViewInit(): void {
    if (!this.scrollSentinel) {
      return;
    }

    // Root is the viewport: the layout's scrolling element clips the sentinel,
    // so it only intersects once the reader has actually reached the bottom.
    // The margin starts the next page slightly early so scrolling stays smooth.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadMore();
        }
      },
      { rootMargin: '200px' },
    );

    this.observer.observe(this.scrollSentinel.nativeElement);
  }

  override ngOnDestroy(): void {
    this.observer?.disconnect();
    super.ngOnDestroy();
  }

  protected get hasMore(): boolean {
    return this.currentPage < this.lastPage;
  }

  protected get isEmpty(): boolean {
    return this.hasLoadedOnce && !this.loading && this.showRounds.length === 0;
  }

  /**
   * Whether any filter is narrowing the list, so the empty state can say
   * "nothing matched" rather than "nothing is booked".
   */
  protected get isFiltered(): boolean {
    return !!this.searchControl.value?.trim() || this.venueControl.value != null;
  }

  protected loadMore(): void {
    if (!this.hasMore || this.loading) {
      return;
    }

    this.load(this.currentPage + 1);
  }

  /**
   * TVG-339: Tailwind classes for a review answer pill. Great and Yes read as
   * good news; Bad and No are the ones the sales team needs to spot on a
   * schedule they are scrolling through.
   */
  protected reviewClasses(value: string | null): string {
    return showRoundReviewClasses(value);
  }

  protected retry(): void {
    this.load(this.currentPage);
  }

  /** A filter change restarts the list from page one. */
  private reload(): void {
    this.currentPage = 1;
    this.lastPage = 1;
    this.load();
  }

  private load(pageNum = 1): void {
    this.loading = true;
    this.loadError = null;

    this.showRoundService
      .list({
        search: this.searchControl.value?.trim() || null,
        venueId: this.venueControl.value,
        pageNum,
        pageSize: this.pageSize,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const rows = response.data ?? [];
          this.showRounds = pageNum === 1 ? rows : [...this.showRounds, ...rows];
          this.currentPage = response.meta?.current_page ?? pageNum;
          this.lastPage = response.meta?.last_page ?? pageNum;
          this.total = response.meta?.total ?? this.showRounds.length;
          this.loading = false;
          this.hasLoadedOnce = true;
        },
        error: (error) => {
          this.loading = false;
          this.hasLoadedOnce = true;
          this.loadError = error?.error?.message || 'Failed to load viewings';
        },
      });
  }

  private loadVenues(): void {
    this.venueService
      .getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.venueChoices = (response?.data ?? []).map((venue: { id: number; name: string }) => ({
            value: venue.id,
            label: venue.name,
          }));
        },
        // A venue list that fails to load costs the filter, not the page.
        error: () => {
          this.venueChoices = [];
        },
      });
  }
}
